import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { useSelectRow } from '../utils/useSelectRow';
import useDataFetch from '../hooks/useDataFetch';
import { useCategories } from '../hooks/useCategories';
import { getFields, searchFields } from '../utils/fields';
import SearchForm from '../components/SearchForm';
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import RelatedMenu from '../components/RelatedMenu';
import { TextField, MenuItem, Stack, Checkbox, Link, Typography, Button, Paper, Box, Chip } from '@mui/material';
import { Sync } from '@mui/icons-material';
const InventoryTransactionRegisterPage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState([]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await axiosInstance.get('/warehouse', {
          params: { activeOnly: true }
        });
        setWarehouses(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(err);
        }
      }
    };
    fetchWarehouses();
  }, []);

  const {
    dataset,
    error,
    currentPage,
    totalPages,
    searchQuery,
    setSearchQuery,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handlePageChange,
    refetch
  } = useDataFetch('/inventory', { keywords: '', category_id: '' });
  const { control, handleSubmit, setValue, reset } = useForm({
    defaultValues: {}
  });
  // useSelectRow フックを使って選択状態を管理
  const { selectedItems, setSelectedItems, handleRowSelect, handleSelectAll } = useSelectRow('inventory_key');
  const [bulkOperation, setBulkOperation] = useState('');
  // 行ごとの状態を管理するための状態
  const categories = useCategories();

  // 一括操作
  const handleBulkOperation = (transactionType) => {
    selectedItems.forEach((key) => {
      setValue(`transactionType_${key}`, transactionType);
    });
    setBulkOperation(transactionType);
  };

  // フォーム送信
  const onSubmit = async (data) => {
    const transactions = dataset.map((item) => {
      const quantity = parseInt(data[`inventory_${item.inventory_key}`], 10);
      const transaction_type = data[`transactionType_${item.inventory_key}`];

      return {
        variant_id: item.variant_id,
        warehouse_id: item.warehouse_id,
        quantity,
        transaction_type,
        order_id: item.order_id || null,
        inventory_key: item.inventory_key,
      };
    }).filter(tx => tx.quantity > 0 && tx.transaction_type);

    if (transactions.length === 0) {
      showSnackbar('在庫情報が入力されていません', 'warning');
      return;
    }

    try {
      const res = await axiosInstance.post('/inventory/transactions', { transactions });

      if (res.data.skipped && res.data.skipped.length > 0) {
        showSnackbar(`一部のデータは登録できませんでした (${res.data.skipped.length}件)`, 'warning');
        const skippedKeys = res.data.skipped.map(s => s.tx.inventory_key);

        // 成功した分だけフォームの値をクリア
        dataset.forEach(item => {
          if (!skippedKeys.includes(item.inventory_key)) {
            setValue(`inventory_${item.inventory_key}`, '');
            setValue(`transactionType_${item.inventory_key}`, '');
          }
        });
      } else {
        // 全件成功なら全部リセット
        reset();
        setSelectedItems([]);
        setBulkOperation('');
        showSnackbar(res?.data?.message || '在庫情報を登録しました', 'success');
      }

      refetch();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || '登録に失敗しました', 'error');
    }
  };

  // 検索フィールド定義
  const fieldsToInclude = ['keywords', 'category_id'];
  let searchItems = getFields(searchFields, fieldsToInclude);

  // カラム定義
  const columns = [
    {
      label: (
        <Checkbox
          checked={dataset.length > 0 && selectedItems.length === dataset.length}
          indeterminate={selectedItems.length > 0 && selectedItems.length < dataset.length}
          onChange={(e) => handleSelectAll(e, dataset)}
        />
      ),
      accessor: 'checkbox',
      render: (item) => (
        <Checkbox
          checked={selectedItems.includes(item.inventory_key)}
          onChange={() => handleRowSelect(item.inventory_key)}
        />
      ),
      width: '50px',
    },
    ...(hasLimit('ec') ? [{
      label: '',
      accessor: 'image_url',
      render: (item) => {
        return (
          <img
            alt={item.product_name || '商品画像'}
            src={
              item.image_url
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url.replace(/\\/g, '/')}`
                : `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`
            }
            className="product-image"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NO_IMAGE}`;
            }}
          />
        );
      },
      width: '60px',
    }] : []),
    {
      label: '品番',
      accessor: 'sku',
      render: (item) => (
        <Link
          onClick={() => navigate(`/products/${item.product_id}`)}>
          {item.sku}
        </Link>
      ),
      width: '150px',
    },
    {
      label: '商品名',
      accessor: 'product_name',
      width: '350px',
      render: (item) => (
        <Box>
          <Typography>{item.product_name}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {item?.option_combination &&
              Object.entries(item.option_combination).map(([type, value]) => (
                <React.Fragment key={type}>
                  {" "}
                  {type}: {value}
                </React.Fragment>
              ))}
          </Typography>
        </Box>
      )
    },
    { label: '倉庫', accessor: 'warehouse_name', minWidth: '100px' },
    { label: '在庫数', accessor: 'logical_stock', minWidth: '100px', },
    {
      label: '在庫の調整',
      accessor: '',
      render: (item) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Controller
            name={`inventory_${item.inventory_key}`}
            control={control}
            defaultValue=""
            rules={{
              validate: (v) => {
                if (isNaN(Number(v))) return '数値を入力してください';
                if (Number(v) < 0) return '0以上を入力してください';
                if (String(Math.floor(Number(v))).length > 9) return '9桁以内で入力してください';
                return true;
              }
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                placeholder="数量"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ width: 150, minWidth: 150 }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 9
                }}
              />
            )}
          />
          <Controller
            name={`transactionType_${item.inventory_key}`}
            control={control}
            defaultValue=""
            render={({ field, fieldState }) => (
              <TextField
                select
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: false }}
                sx={{ width: 100, minWidth: 100 }}
              >
                <MenuItem value="" disabled>操作</MenuItem>
                <MenuItem value="入庫">入庫</MenuItem>
                <MenuItem value="出庫">出庫</MenuItem>
                {/*<MenuItem value="生産">生産</MenuItem>*/}
              </TextField>
            )}
          />
        </Stack>
      ),
      minWidth: '250px',
    },
  ];
  const links = [
    { path: '/products', label: '在庫一覧', icon: 'inventory_2' },
    { path: '/inventory/transactions', label: '入出庫履歴', icon: 'history' },
    { path: '/products/new', label: '新規商品登録', icon: 'post_add' },
  ];
  useEffect(() => {
    handleSearchSubmit(); // 最新のwarehouse_idsで検索
  }, [searchQuery.warehouse_ids]);

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" component="h1">入出庫登録</Typography>
      {/* <button type="button">
        CSVで登録する
      </button> */}
      <div className='grid2-2_1'>
        {/* 検索フォーム */}
        <SearchForm
          searchQuery={searchQuery}
          searchFields={searchItems}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleClearSearch={handleClearSearch}
          categories={categories}
        />
        <RelatedMenu links={links} />
      </div>
      <Paper variant='section'>
        <Stack direction="row" justifyContent='space-between' spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            value={bulkOperation || ''}
            onChange={(e) => handleBulkOperation(e.target.value)}
            sx={{ width: 300 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: false }}
          >
            <MenuItem value="" disabled>選択した商品の操作の一括設定</MenuItem>
            <MenuItem value="入庫">入庫</MenuItem>
            <MenuItem value="出庫">出庫</MenuItem>
            {/*<MenuItem value="生産">生産</MenuItem>*/}
          </TextField>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems='center' justifyContent='end' sx={{ mb: 2 }}>
            <Typography variant="caption">倉庫で絞り込み</Typography>
            {warehouses.map((w) => (
              <Chip
                key={w.warehouse_id}
                label={w.warehouse_name}
                size='medium'
                clickable
                color={warehouseFilter.includes(w.warehouse_id) ? 'primary' : 'default'}
                onClick={() => {
                  const warehouseId = w.warehouse_id;
                  const newFilter = warehouseFilter.includes(warehouseId)
                    ? warehouseFilter.filter(id => id !== warehouseId)
                    : [...warehouseFilter, warehouseId];

                  setWarehouseFilter(newFilter);
                  setSearchQuery(prev => ({
                    ...prev,
                    warehouse_ids: newFilter,
                  }));
                }}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Stack>

        <TableView dataset={dataset} columns={columns} />

        {/* ページネーション */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Paper>
      <div className='botton-row'>
        <Button
          type='submit'
          variant="contained"
          color="primary"
          startIcon={<Sync />}
          onClick={handleSubmit(onSubmit)}
        >
          在庫情報を登録する
        </Button>
      </div>
    </>
  );
};

export default InventoryTransactionRegisterPage;
